import type { IperiaSessionStartBounds, IperiaStatusPayload } from '../types/iperiaStatus'

export const IPERIA_STEP_PATHS = ['verification', 'nir', 'documents', 'parent-employeur', 'signature', 'success'] as const

export type IperiaStep = (typeof IPERIA_STEP_PATHS)[number]

const STEP_ORDER: IperiaStep[] = [...IPERIA_STEP_PATHS]

export function getStepFromPath(pathname: string): IperiaStep | null {
  const match = pathname.match(/\/iperia\/[^/]+\/([^/?#]+)/)
  if (!match) return null

  const segment = match[1]
  return IPERIA_STEP_PATHS.includes(segment as IperiaStep) ? (segment as IperiaStep) : null
}

const AM_SIGNED_STATUSES = [
  'awaiting_pe_signature',
  'pe_signature_expired',
  'pe_signed',
  'validated',
  'completed',
]

export function hasAmSigned(status: IperiaStatusPayload): boolean {
  return Boolean(status.bi_am_signed_at) || AM_SIGNED_STATUSES.includes(status.workflow_status)
}

export function getActiveStep(status: IperiaStatusPayload): IperiaStep {
  // Verification must be completed before any later step is considered active.
  if (!status.learner_summary_confirmed || ['draft'].includes(status.workflow_status)) {
    return 'verification'
  }
  if (['awaiting_nir'].includes(status.workflow_status)) {
    return 'nir'
  }
  if (['awaiting_am_documents', 'documents_under_review', 'documents_rejected'].includes(status.workflow_status)) {
    return 'documents'
  }
  if (['awaiting_parent_employeur'].includes(status.workflow_status)) {
    return 'parent-employeur'
  }
  if (['awaiting_bi_signature', 'bi_blocked_late_start', 'awaiting_pe_signature', 'pe_signature_expired'].includes(status.workflow_status)) {
    return 'signature'
  }
  if (['pe_signed', 'validated', 'completed'].includes(status.workflow_status)) {
    return 'success'
  }
  return 'verification'
}

export function canAccessStep(status: IperiaStatusPayload, step: IperiaStep): boolean {
  if (!status.learner_summary_confirmed || ['draft'].includes(status.workflow_status)) {
    return step === 'verification'
  }

  if (['awaiting_nir'].includes(status.workflow_status)) {
    if (step === 'verification') return true
    if (step === 'nir') return status.can_proceed_to_nir
    return false
  }

  if (['awaiting_am_documents', 'documents_under_review', 'documents_rejected'].includes(status.workflow_status)) {
    if (step === 'verification') return true
    if (step === 'nir') return status.can_proceed_to_nir
    if (step === 'documents') return status.can_proceed_to_documents
    return false
  }

  if (['awaiting_parent_employeur'].includes(status.workflow_status)) {
    if (step === 'verification') return true
    if (step === 'nir') return status.can_proceed_to_nir
    if (step === 'documents') return status.can_proceed_to_documents
    if (step === 'parent-employeur') return status.can_proceed_to_parent_employeur
    return false
  }

  if (['awaiting_bi_signature', 'bi_blocked_late_start', 'awaiting_pe_signature', 'pe_signature_expired'].includes(status.workflow_status)) {
    if (step === 'verification') return true
    if (step === 'nir') return status.can_proceed_to_nir
    if (step === 'documents') return status.can_proceed_to_documents
    if (step === 'parent-employeur') return status.can_proceed_to_parent_employeur
    if (step === 'signature') return status.can_sign_bi || hasAmSigned(status) || status.workflow_status === 'bi_blocked_late_start' || !!status.signature_blocked_message
    if (step === 'success') return hasAmSigned(status)
    return false
  }

  if (['pe_signed', 'validated', 'completed'].includes(status.workflow_status)) {
    return true
  }

  return false
}

/**
 * La date de début de session n'appartient à l'apprenant.e que sur un dossier encore en brouillon :
 * confirmer le récapitulatif fait avancer le `workflow_status` côté CRM, et la date sert alors de
 * base aux traitements aval. Après ça le parcours ne la renvoie plus, sinon un dossier dont la date
 * est passée sous le délai minimum se ferait refuser par l'API sans que personne y ait touché.
 */
export function canEditSessionStartDate(status: IperiaStatusPayload): boolean {
  if (typeof status.can_edit_session_start_date === 'boolean') return status.can_edit_session_start_date
  return status.workflow_status === 'draft' && !status.learner_summary_confirmed
}

/** Raison pour laquelle une date n'est pas sélectionnable, `null` si elle l'est. */
export function sessionStartDateError(value: string, bounds?: IperiaSessionStartBounds | null): string | null {
  if (!bounds || !value) return null
  const format = (date: string) => date.split('-').reverse().join('/')
  if (value < bounds.min) return `La formation ne peut pas commencer avant le ${format(bounds.min)}.`
  if (value > bounds.max) return `La formation ne peut pas commencer après le ${format(bounds.max)}.`
  if (bounds.disabled_ranges.some((range) => value >= range.start && value <= range.end)) {
    return 'Une formation démarrant après le 10 décembre se termine l\'année suivante, ce que l\'IPERIA ne finance pas.'
  }
  return null
}

/**
 * Une pièce obligatoire refusée alors que le dossier a déjà passé l'étape des pièces (bulletin déjà
 * émis, parent employeur remplacé depuis le CRM) : le statut reste sur la signature, mais c'est la
 * pièce que l'AM doit d'abord remplacer. Sans ça, le lien de l'email de refus ouvrait la signature,
 * qui ne dit rien de la pièce.
 */
export function hasRejectedRequiredDocument(status: IperiaStatusPayload): boolean {
  return (status.documents ?? []).some((document) => document.required && document.is_rejected)
}

export function getDefaultRouteForStatus(status: IperiaStatusPayload): IperiaStep {
  if (hasRejectedRequiredDocument(status) && canAccessStep(status, 'documents')) {
    return 'documents'
  }
  const activeStep = getActiveStep(status)
  const activeIndex = STEP_ORDER.indexOf(activeStep)

  for (let index = activeIndex; index >= 0; index -= 1) {
    const step = STEP_ORDER[index]
    if (canAccessStep(status, step)) {
      return step
    }
  }

  return 'verification'
}
